import React, {
    ReactElement, useEffect, useMemo, useState,
} from "react";
import { Form } from "react-bootstrap";
import { RiInboxArchiveLine } from "@remixicon/react";
import useKeyboardShortcuts from "../../../contexts/KeyboardShortcutContext";
import { Ticket } from "../../../contexts/selection-state/selection-state";
import useSelectionState from "../../../contexts/selection-state/SelectionStateContext";
import {
    ChatStatus,
    CustomFieldValueInput, FieldTypedValue, FormFieldReplacementInput, RequiredField, Ticket as RawTicket,
    TicketInput, useCloseSettingsQuery, useEndChatMutation, useGetChatQuery, useSaveTicketMutation,
} from "../../../__generated__/api";
import Confirm from "../../confirm/Confirm";
import FormDisplay, { TAGS_ID } from "../../form/Form";
import Loading from "../../loading/Loading";
import Select, { SelectInstanceHandler } from "../../select/Select";
import { useRegisterAction, useTicketState } from "../../../contexts/ticket-state";
import {
    ActionType, ActionHandler, HANDLER_INTERFACE, ActionContext,
} from "../../../contexts/ticket-state/action-registry";
import Button from "../../theme/button/Button";
import Icon from "../../theme/icon/Icon";
import Text from "../../theme/text/Text";

const AI_TAGS_FIELD = "urn:fq:artificial-intelligence-field:tags";
export default function EndChat(): ReactElement {
    const selectionState = useSelectionState();
    const [selectedTickets, setSelectedTickets] = useState<Ticket[]>([]);

    const { data: chatValues } = useGetChatQuery({
        skip: !selectedTickets.firstElement?.id,
        variables: {
            id: selectedTickets.firstElement?.id,
        },
    });

    const [saveTicket] = useSaveTicketMutation();
    const ticketContext = useTicketState();

    const { data, loading } = useCloseSettingsQuery();
    const [sendEndChat] = useEndChatMutation();

    const [shouldShow, setShouldShow] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");
    const [tags, setTags] = useState<string[]>([]);
    const [ticketInput, setTicketInput] = useState<TicketInput>();
    const [actionHandler, setActionHandler] = useState<ActionHandler>();

    const [selectedTicketIds, setSelectedTicketIds] = useState<string[]>([]);
    const selectRef = React.createRef<SelectInstanceHandler>();

    const requiredFields = useMemo(() => new Set(data?.settings?.customization?.requiredCloseFields ?? []), [data]);

    useEffect(() => {
        setTags(chatValues?.chat?.ticket?.tags ?? []);
    }, [chatValues?.chat?.ticket?.tags]);

    const startEndChat = async (tickets?: Ticket[]) => {
        setShouldShow(true);
        selectRef.current?.focus();

        const selected = tickets
            ?? ticketContext?.selectionState.current?.map(row => row.chat as Ticket)
            ?? selectionState
                .getSelection()
                .filter(ticket => ticket.status === ChatStatus.Assigned
                    || ticket.status === ChatStatus.OnHold
                    || ticket.status === ChatStatus.Snoozed);
        setSelectedTickets(selected);
        setSelectedTicketIds(selected.toIdList("id"));
    };

    selectionState?.registerEndChatHandler((tickets?: Ticket[]) => {
        startEndChat(tickets);
    });

    useRegisterAction({
        createBulkActionButton(items, context) {
            return context === ActionContext.BULK_ACTION && items.some(row => row.status !== ChatStatus.Finished) ? {
                sortIndex: 1000,
                text: "Close Tickets",
                icon: <RiInboxArchiveLine />,
                variant: "danger",
                canNotOverflow: true,
            } : undefined;
        },
        handleBulkAction(handler) {
            const handlerTags = handler.items.find(row => row.ticket.tags?.isNotEmpty)?.ticket.tags;
            if (handlerTags) {
                setTags(tags);
            }
            startEndChat(handler.items.map(row => row.chat));
            setActionHandler(handler);
        },
    }, ActionType.CLOSE);

    useEffect(() => {
        if (!shouldShow) {
            setTags([]);
        } else {
            setTags(chatValues?.chat?.ticket?.tags ?? []);
        }
        setMessage("");

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldShow]);

    const endChat = async () => {
        actionHandler?.items.forEach(row => {
            row.setTicketStateIdLocal(undefined, ChatStatus.Finished);
            row.closeIfOpen();
        });
        actionHandler?.done();

        if (ticketInput) {
            await saveTicket({
                variables: {
                    id: selectedTickets.firstElement.ticket?.id,
                    ticket: ticketInput,
                },
            });
        }

        // selectionState.delete(...selectedTicketIds);
        setShouldShow(false);
        selectionState?.closeCurrent();
        selectionState?.setChatStatuses(selectedTicketIds, ChatStatus.Finished);
        await sendEndChat({
            variables: {
                chatEndMessages: selectedTicketIds.map(chatId => ({
                    chatId,
                    message,
                    tags,
                })),
            },
        });
    };

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "k+e",
            context: "default",
            description: "Close the ticket",
            group: "Conversations",
            handler: () => {
                startEndChat();
                setActionHandler(ticketContext?.selectionState.createActionHandler(ActionContext.BULK_ACTION));
            },
        });
    });

    const handleKeyDown = (key: React.KeyboardEvent<HTMLElement>) => {
        if (key.key === "Escape") {
            selectRef.current?.blur();
        }
    };

    const handleChange = (ticket: TicketInput) => {
        const copy = { ...ticketInput, ...ticket, customField: undefined };
        copy.customFields = (ticketInput?.customFields ?? []).concat(
            ticket.customFields as CustomFieldValueInput[],
            [ticket.customField as CustomFieldValueInput],
        ).filterNull().dedupe(row => row.formFieldId, (_left, right) => right);
        setTicketInput(copy);
    };

    const ticket = selectedTickets.firstElement?.ticket as RawTicket;
    const aITicket = ticket?.artificialIntelligence?.values?.find(item => item.id === AI_TAGS_FIELD);

    return (
        <Confirm
            confirmText="Close Ticket"
            shouldShow={shouldShow}
            closeText="Close"
            title="Close Ticket"
            onHide={() => {
                setShouldShow(false);
                actionHandler?.rollback();
            }}
            onConfirm={() => endChat()}
        >
            {selectedTickets.isEmpty || loading ? (
                <div className="overflow-auto w-100 h-100">
                    <div className={`centered-container-lg m-auto w-100 h-100 flex-stretch-0 `}>
                        <Loading />
                    </div>
                </div>
            )
                : (
                    <div className="flex-grow-1 d-flex p3-1 flex-column">

                        <FormDisplay
                            isFormField
                            ticket={chatValues?.chat?.ticket as RawTicket}
                            isEditable
                            isEditing
                            onChange={handleChange}
                            shouldFilterAgentRequired
                            descriptionMessage={(
                                <small className="text-danger mb-3 d-block">
                                    The following fields are required to be filled out to close this ticket.
                                </small>
                            )}
                            className="border-bottom rounded mt-2 border border-danger rounded px-3 py-2 mb-4"
                            skipFields={[
                                TAGS_ID,
                            ]}
                        />
                        <Form.Label>
                            Tags
                            {" "}
                            {!requiredFields.has(RequiredField.Tags) && <small>(optional)</small>}
                        </Form.Label>
                        <Select
                            ref={selectRef}
                            placeholder="Add Tags"
                            isMulti
                            minRequired={requiredFields.has(RequiredField.Tags) ? 1 : 0}
                            options={data?.settings?.customization?.tags?.map(tag => ({
                                label: tag,
                                value: tag,
                                data: "",
                            })) ?? []}
                            onKeyDown={handleKeyDown}
                            onChange={selected => setTags(selected.map(row => row.value))}
                            value={tags.map(tag => ({
                                label: tag,
                                value: tag,
                            }))}
                        />
                        {aITicket?.value && (
                            <div className="mt-1">
                                <Button.ArtificialIntelligence
                                    onClick={() => setTags(aITicket.value.options as string[])}
                                    variant="ghost-blue"
                                    size="xsmall"
                                >
                                    AI Tags:
                                    {" "}
                                    <Text as="span" textVariant="caption-regular" colorVariant="body-secondary">
                                        {aITicket.value.options?.join(", ")}
                                    </Text>
                                </Button.ArtificialIntelligence>
                            </div>
                        )}

                        <Form.Label className="mt-4">
                            Additional Comments
                            {"  "}
                            {!requiredFields.has(RequiredField.TicketNotes) && <small>(optional)</small>}
                        </Form.Label>

                        <Form.Control
                            as="textarea"
                            type="text"
                            required={requiredFields.has(RequiredField.TicketNotes)}
                            placeholder="Resolved this ticket by..."
                            className="default-text-height p-2"
                            onChange={evt => setMessage(evt.target.value)}
                        />
                        <Form.Text>
                            * This note will appear in the chat and only visible to agents.
                        </Form.Text>

                    </div>
                )}
        </Confirm>
    );
}

HANDLER_INTERFACE.register(EndChat);

EndChat.defaultProps = {
    refreshData: () => {
        // nothing
    },
    onChange: (value: FieldTypedValue, replacements?: FormFieldReplacementInput[]) => {
        // nothing
    },
};
